import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GeneralModal,
  GeneralModalNoButtonVariant,
  GeneralModalYesButtonVariant,
  SvgIcon,
} from '../../../Molecules';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Icons, IconType } from '../../../../constants/icons';
import styles from './MemoryBoostConfigModal.module.css';

type MemoryBoostConfigModalProps = {
  useMemoryBoost: boolean;
  memoryBoostDays: number;
  onPrimary: (isBoostOn: boolean, period: number) => void;
  onPressCancel: () => void;
  onPressBack: () => void;
  onPressInfo: () => void;
};

export const MemoryBoostConfigModal = (props: MemoryBoostConfigModalProps) => {
  const {
    useMemoryBoost,
    memoryBoostDays,
    onPrimary,
    onPressCancel,
    onPressBack,
    onPressInfo,
  } = props;
  const { t } = useTranslation();
  const [isBoostOn, setBoost] = useState(useMemoryBoost);
  const [period, setPeriod] = useState(memoryBoostDays);

  const handleBoostToggle = () => {
    setBoost(!isBoostOn);
  };

  const updateSliderBackground = () => {
    const slider = document.querySelector(
      `.${styles.rangeSlider}`,
    ) as HTMLInputElement;
    if (slider) {
      const value = Number(slider.value);
      const min = Number(slider.min) || 0;
      const max = Number(slider.max) || 100;
      const percentage = ((value - min) / (max - min)) * 100;
      slider.style.background = `linear-gradient(to right, #000000 0%, #000000 ${percentage}%, #d3d3d3 ${percentage}%, #d3d3d3 100%)`;
    }
  };

  useEffect(() => {
    updateSliderBackground();
  }, []);

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPeriod(Number(event.target.value));
    updateSliderBackground();
  };

  return (
    <GeneralModal
      isYesNo={true}
      onYes={() => {
        onPrimary(isBoostOn, period);
      }}
      onClose={onPressCancel}
      buttonSetting={{
        yesText: t('OK'),
        noText: t('Cancel'),
        noVariant: GeneralModalNoButtonVariant.Gray,
        yesVariant: GeneralModalYesButtonVariant.Cyan,
      }}
    >
      <div className={styles.memoryBoost}>
        <div className={styles.centerContainer}>
          <div className={styles.titleRow}>
            <SvgIcon
              name={SVGIconType.Arrow}
              className={styles.arrow}
              stroke={'#000000'}
              onClick={onPressBack}
            />
            <p className={styles.title}>{t('JOYAJOY AI Memory Boost')}</p>
            <img
              className={styles.titleIcon}
              src={Icons[IconType.GameInstructionLine]}
              onClick={onPressInfo}
            />
          </div>
          <p className={styles.subtitle}>
            {t(
              'Use Cases: Examination, dictation, recitation, training, speech, lyrics, poems, etc.',
            )}
          </p>
          <hr />
          <div className={styles.boostContainer}>
            <div className={styles.boostRow}>
              <SvgIcon
                name={SVGIconType.Ai}
                className={styles.aiIcon}
                stroke={'#000000'}
                fill={'#000000'}
                color={'#000000'}
              />
              <span className={styles.boostText}>{t('AI Memory Boost')}</span>
              <div className={styles.toggleContainer}>
                <span className={styles.toggleText}>
                  {isBoostOn ? 'ON' : 'OFF'}
                </span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={isBoostOn}
                    onChange={handleBoostToggle}
                  />
                  <span className={styles.slider} />
                </label>
              </div>
            </div>
          </div>
          <p className={styles.boostDescription}>
            {t('Enhance your memory retention by up to 9 times.')}
          </p>
          <h2 className={styles.periodTitle}>{t('Period')}</h2>
          <div
            className={`${styles.periodContainer} ${
              !isBoostOn ? styles.disabledOverlay : ''
            }`}
          >
            <div className={styles.periodDayContainer}>
              <div className={styles.periodValue}>
                <span className={styles.days}>{period}</span>
              </div>
              <span className={styles.daysText}>{t('days')}</span>
            </div>
            <div className={styles.sliderContainer}>
              <span>3</span>
              <input
                type="range"
                min="3"
                max="60"
                value={period}
                onChange={handlePeriodChange}
                className={styles.rangeSlider}
                disabled={!isBoostOn}
              />
              <span>60</span>
            </div>
          </div>
          <p className={styles.footer}>
            {t(
              'Just 55 days, your long-term memory can be extended up to 2 years!',
            )}
          </p>
        </div>
      </div>
    </GeneralModal>
  );
};
